import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const TIER_CONFIG = {
  CRITICAL: { color: '#c0392b', bg: '#fdf2f2', label: 'Critical', emoji: '🔴' },
  HIGH:     { color: '#e67e22', bg: '#fef9f0', label: 'High',     emoji: '🟠' },
  MODERATE: { color: '#f39c12', bg: '#fffdf0', label: 'Moderate', emoji: '🟡' },
  LOW:      { color: '#27ae60', bg: '#f0faf4', label: 'Low',      emoji: '🟢' },
};

function TierBadge({ tier }) {
  const cfg = TIER_CONFIG[tier] || TIER_CONFIG.LOW;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.color}33`,
      borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700,
    }}>
      {cfg.emoji} {cfg.label}
    </span>
  );
}

function ScoreBar({ score }) {
  const color = score >= 80 ? '#c0392b' : score >= 60 ? '#e67e22' : score >= 30 ? '#f39c12' : '#27ae60';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${score}%`, height: '100%', background: color, borderRadius: 3,
                      transition: 'width 0.6s ease' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, width: 32, textAlign: 'right' }}>{score}</span>
    </div>
  );
}

const TIER_FILTERS = [
  { key: '',         label: 'All Tiers' },
  { key: 'CRITICAL', label: '🔴 Critical' },
  { key: 'HIGH',     label: '🟠 High' },
  { key: 'MODERATE', label: '🟡 Moderate' },
  { key: 'LOW',      label: '🟢 Low' },
];

export default function RiskDashboard() {
  const [scores, setScores]     = useState([]);
  const [tier, setTier]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [batchLoading, setBatch]= useState(false);
  const [summary, setSummary]   = useState({ total: 0, counts: { CRITICAL: 0, HIGH: 0, MODERATE: 0, LOW: 0 } });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ai/risk-scores${tier ? `?tier=${tier}` : ''}`);
      const data = res.data;
      setScores(data.scores || []);

      // Compute summary from full list (load all for summary)
      const all = await api.get('/ai/risk-scores');
      const allScores = all.data.scores || [];
      const counts = { CRITICAL: 0, HIGH: 0, MODERATE: 0, LOW: 0 };
      allScores.forEach(s => { if (counts[s.tier] !== undefined) counts[s.tier]++; });
      setSummary({ total: allScores.length, counts });
    } catch {
      setScores([]);
    } finally {
      setLoading(false);
    }
  }, [tier]);

  useEffect(() => { load(); }, [load]);

  const runBatch = async () => {
    setBatch(true);
    try {
      const res = await api.post('/ai/risk-score/batch/all-active');
      alert(`Scored ${res.data.scored} patients successfully.`);
      await load();
    } catch {
      alert('Batch scoring failed');
    } finally {
      setBatch(false);
    }
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#2b1b22' }}>
            Rehospitalization Risk
          </h1>
          <p style={{ margin: '4px 0 0', color: '#8b7681', fontSize: 13 }}>
            AI-powered 30-day rehospitalization risk scoring · LACE index + home health factors
          </p>
        </div>
        <button
          onClick={runBatch}
          disabled={batchLoading}
          className="hhms-btn"
          style={{
            background: '#7b1e3c', color: '#fff', border: 'none',
            borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700,
            cursor: batchLoading ? 'not-allowed' : 'pointer', opacity: batchLoading ? 0.6 : 1,
          }}
        >
          {batchLoading ? 'Scoring…' : '⚡ Score All Active Patients'}
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {Object.entries(TIER_CONFIG).map(([key, cfg]) => (
            <div
              key={key}
              onClick={() => setTier(tier === key ? '' : key)}
              className="hhms-card"
              style={{
                background: tier === key ? cfg.bg : '#fff',
                border: `2px solid ${tier === key ? cfg.color : '#ece3e7'}`,
                borderRadius: 12, padding: '16px 20px', cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 24 }}>{cfg.emoji}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: cfg.color, lineHeight: 1.1 }}>
                {summary.counts[key]}
              </div>
              <div style={{ fontSize: 12, color: '#8b7681', fontWeight: 600, marginTop: 2 }}>
                {cfg.label} Risk
              </div>
            </div>
          ))}
      </div>


      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TIER_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setTier(f.key)}
            style={{
              padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              border: `1px solid ${tier === f.key ? '#7b1e3c' : '#ddd'}`,
              background: tier === f.key ? '#7b1e3c' : '#fff',
              color: tier === f.key ? '#fff' : '#555', cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#8b7681', alignSelf: 'center' }}>
          {scores.length} patients shown
        </span>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ece3e7', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f5f6' }}>
              {['Patient', 'Risk Score', 'Tier', 'Factors', 'Next Review', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#8b7681', fontSize: 11, textTransform: 'uppercase' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#8b7681' }}>
                  Computing risk scores…
                </td>
              </tr>
            ) : scores.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#8b7681' }}>
                  No risk scores found. Click "Score All Active Patients" to compute scores.
                </td>
              </tr>
            ) : (
              scores.map((s, i) => (
                <tr
                  key={s.patient_id}
                  className="hhms-table-row"
                  style={{ borderTop: i === 0 ? 'none' : '1px solid #f0ebed' }}
                >
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: '#2b1b22' }}>
                    {s.patient_name || s.patient_id.slice(0, 8) + '…'}
                  </td>
                  <td style={{ padding: '10px 14px', minWidth: 160 }}>
                    <ScoreBar score={s.score} />
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <TierBadge tier={s.tier} />
                  </td>
                  <td style={{ padding: '10px 14px', color: '#555', fontSize: 12 }}>
                    {s.factors ? `${s.factors.length} factor(s)` : '—'}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#8b7681', fontSize: 12 }}>
                    {s.calculated_at ? new Date(s.calculated_at).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <button
                      onClick={() => navigate(`/patient-360/${s.patient_id}`)}
                      style={{
                        background: 'none', border: '1px solid #ddd',
                        borderRadius: 6, padding: '4px 10px', fontSize: 11,
                        cursor: 'pointer', color: '#7b1e3c', fontWeight: 600,
                      }}
                    >
                      View Patient
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Risk factor legend */}
      <div style={{ marginTop: 20, padding: 16, background: '#fafafa', borderRadius: 10, border: '1px solid #ece3e7', fontSize: 12, color: '#666' }}>
        <strong style={{ color: '#2b1b22' }}>Scoring factors:</strong> Recent hospitalization (25 pts) · Multiple comorbidities (20 pts) · Low SpO2 (8–15 pts) · Elevated heart rate (10 pts) · Missed visits (15 pts) · OASIS functional decline (15 pts) · Recent ED visits (15 pts)
        <br />
        <strong style={{ color: '#2b1b22' }}>Tiers:</strong> Low 0–29 · Moderate 30–59 · High 60–79 · Critical 80+
      </div>
    </div>
  );
}
